import {
  GAME_ID,
  GAME_MODES,
  REDUX_ACTIONS,
  FULL_BATTERY_LIFES_COUNT,
  ANIMATIONS,
  CORRECT_ITEMS_TO_MASTER,
} from '../../data/constants';
import { isLastQuestion } from '../selectors/roundPlaySelectors';

const initState = {
  gameMode: GAME_MODES.INACTIVE,
  // Curriculum Pointers
  levelIndex: 0,
  questionIndex: 0,
  pageIndex: 0, // Counts each incremental page within a single createStory type of question
  questionItemsInRound: [],

  isSortingCreatedStory: false, // Track whether user still needs to sort the story they just helped create
  progressAmount: 0, // The percentage amount of the round that user has completed
  responseAttemptCount: 0,
  selectedResponse: undefined,
  introScreenTextIndex: 0, // What index of screen are we on in the intoduction
  batteryLifesCount: FULL_BATTERY_LIFES_COUNT, // count of the bars in the batery (round lifes)
  inLevelWithCreateStory: false,
  animationToPlay: ANIMATIONS.BROKEN_COMPUTER,
};

const roundPlayReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.INITIALIZE_ROUND:
      return {
        ...state,
        questionIndex: 0,
        pageIndex: 0,
        questionItemsInRound: payload.questionItemsInRound,
      };

    case REDUX_ACTIONS.NEXT_STORY_PAGE: {
      return {
        ...state,
        pageIndex: state.pageIndex + 1,
        selectedResponse: undefined,
      };
    }

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM: {
      // If there are no more question items, end the round
      if (isLastQuestion(state)) {
        return {
          ...state,
          gameMode: GAME_MODES.INACTIVE,
          isSortingCreatedStory: false,
          progressAmount: 100,
          selectedResponse: undefined,
        };
      }
      // Else advance to next item
      return {
        ...state,
        gameMode: GAME_MODES.STANDARD_ITEM,
        isSortingCreatedStory: false,
        questionIndex: state.questionIndex + 1,
        pageIndex: 0,
        progressAmount: state.progressAmount + (100 / state.questionItemsInRound.length),
        responseAttemptCount: 0,
        selectedResponse: undefined,
      };
    }

    case REDUX_ACTIONS.ROUND_START:
      return {
        ...state,
        gameMode: GAME_MODES.STANDARD_ITEM,
        isSortingCreatedStory: false,
        levelIndex: payload.levelIndex,
        introScreenTextIndex: 0,
        batteryLifesCount: FULL_BATTERY_LIFES_COUNT,
        responseAttemptCount: 0,
        inLevelWithCreateStory: false,
        animationToPlay: ANIMATIONS.BROKEN_COMPUTER,
      };

    case REDUX_ACTIONS.QUICK_ROUND_START:
      return {
        ...state,
        gameMode: GAME_MODES.STANDARD_ITEM,
        levelIndex: payload.level,
        batteryLifesCount: FULL_BATTERY_LIFES_COUNT,
        responseAttemptCount: 0,
      };

    case REDUX_ACTIONS.QUICK_ROUND_END: {
      const progress = payload ? 100 : 0;
      return {
        ...state,
        gameMode: GAME_MODES.INACTIVE,
        isSortingCreatedStory: false,
        progressAmount: progress,
        selectedResponse: undefined,
        responseAttemptCount: CORRECT_ITEMS_TO_MASTER,
      };
    }

    case REDUX_ACTIONS.ROUND_END:
      // Reset to initial state when round is complete
      return {
        ...state,
        batteryLifesCount: FULL_BATTERY_LIFES_COUNT,
        progressAmount: 0,
        inLevelWithCreateStory: false,
      };

    case REDUX_ACTIONS.CHANGE_SCREEN:
      return {
        ...state,
        introScreenTextIndex: 0,
        selectedResponse: undefined,
      };

    case REDUX_ACTIONS.NEXT_INTRO_TEXT:
      return {
        ...state,
        introScreenTextIndex: state.introScreenTextIndex + 1,
      };

    case REDUX_ACTIONS.SET_ANIMATION:
      return {
        ...state,
        animationToPlay: payload,
      };

    case REDUX_ACTIONS.SHOW_CORRECT_RESPONSE:
      return {
        ...state,
        gameMode: GAME_MODES.FEEDBACK_SHOW_CORRECT,
      };

    case REDUX_ACTIONS.SELECT_FILL_IN_BLANK_RESPONSE:
      return {
        ...state,
        selectedResponse: payload,
      };

    case REDUX_ACTIONS.SELECT_RESPONSE: {
      const { isFirstAttempt, isCorrect, responseId } = payload;
      // proceed to next standard item if user responded correctly
      if (isCorrect) {
        return {
          ...state,
          gameMode: GAME_MODES.FEEDBACK_POSITIVE,
          responseAttemptCount: state.responseAttemptCount + 1,
          selectedResponse: responseId,
        };
      }
      // else, set mode to NEGATIVE FEEDBACK since response was incorrect
      if (isFirstAttempt) {
        return {
          ...state,
          gameMode: GAME_MODES.FEEDBACK_NEGATIVE,
          responseAttemptCount: state.responseAttemptCount + 1,
          selectedResponse: responseId,
          batteryLifesCount: state.inLevelWithCreateStory ? state.batteryLifesCount : state.batteryLifesCount - 1,
        };
      }
      return {
        ...state,
        gameMode: GAME_MODES.FEEDBACK_NEGATIVE,
        responseAttemptCount: state.responseAttemptCount + 1,
        selectedResponse: responseId,
      };
    }

    case REDUX_ACTIONS.SET_GAME_MODE:
      return {
        ...state,
        gameMode: payload,
      };

    case REDUX_ACTIONS.SORT_CREATED_STORY: {
      return {
        ...state,
        isSortingCreatedStory: true,
        pageIndex: 0,
        selectedResponse: undefined,
        inLevelWithCreateStory: true,
      };
    }

    default:
      return state;
  }
};

export default roundPlayReducer;
